import {
  getMaxEnterableAmount,
  getSequencerFee,
  resolveNadoSubaccount,
  useInvalidateNadoMaxWithdrawable,
  useNadoFeeRates,
  useNadoMaxWithdrawable,
} from '@funkit/connect-core/clients/nado'
import { useMemo, useRef } from 'react'
import type { Hex } from 'viem'

import type {
  WithdrawalFlowConfig,
  WithdrawalMethodOption,
} from '../../src/flow/withdrawalFlowConfig'
import { logger } from '../../src/logging/logger'
import { useFunkitTranslation } from '../../src/providers/FunkitI18nProvider'
import { useSourceTokenUsdPrice } from '../../src/withdrawal/useSourceTokenUsdPrice'
import {
  NADO_METHOD_ID,
  NADO_MIN_WITHDRAWAL_TOKENS,
  NADO_MOBILE_SPOT_LEVERAGE,
  NADO_USDT0_SOURCE_TOKEN,
  type NadoWithdrawalFlowConfig,
} from './nadoWithdrawalConfig'
import { createNadoWithdrawalSubmit } from './withdrawalSubmit'

/**
 * The Nado method to hand `beginWithdrawal`, carrying the config it runs, so the
 * SDK never holds Nado config itself.
 *
 * Empty until the subaccount id can be built — every Nado query is keyed on it.
 */
export function useNadoWithdrawal({
  nado,
  config,
}: {
  nado: NadoWithdrawalFlowConfig | undefined
  /** Title, callbacks and wallet carry over. Absent until the host has one. */
  config: WithdrawalFlowConfig | undefined
}): WithdrawalMethodOption[] {
  const { t } = useFunkitTranslation()
  const sourceOption = NADO_USDT0_SOURCE_TOKEN
  const subaccount = useNadoSubaccount(nado)

  const { data: feeRates, isFetched: isFeeSettled } = useNadoFeeRates({
    sender: subaccount,
    logger,
  })
  // Asked rather than derived from a spot balance: only Nado's own accounting
  // sees margin, locked collateral and account health.
  const { data: maxWithdrawableX18, isFetched: isMaxSettled } =
    useNadoMaxWithdrawable({
      productId: subaccount ? sourceOption.productId : undefined,
      sender: subaccount,
      spotLeverage: NADO_MOBILE_SPOT_LEVERAGE,
      logger,
    })
  // `beginWithdrawal` snapshots the config, so the getter reads a ref rather
  // than the value of the render it was built in.
  const maxWithdrawableRef = useRef(maxWithdrawableX18)
  maxWithdrawableRef.current = maxWithdrawableX18

  const sequencerFee = getSequencerFee(feeRates, sourceOption)
  const sourceToken = {
    chainId: sourceOption.tokenChainId,
    address: sourceOption.tokenAddress,
    symbol: sourceOption.symbol,
    decimals: sourceOption.decimals,
  }
  const { data: sourceTokenUsdPrice } = useSourceTokenUsdPrice(sourceToken)
  const invalidateMaxWithdrawable = useInvalidateNadoMaxWithdrawable()
  // `isFetched` is true after a failure too, so settled is not known.
  const hasMaximum = maxWithdrawableX18 !== undefined

  // Withheld while the first answer is still in flight, and permanently without
  // a wallet — Nado withdrawals are signed by the user, so there is no method
  // to offer rather than one that fails at submit.
  if (
    !nado ||
    !config?.withdrawalWallet ||
    !subaccount ||
    !isFeeSettled ||
    !isMaxSettled
  ) {
    return []
  }

  return [
    {
      id: NADO_METHOD_ID,
      title: t('withdrawCrypto.methodTitle'),
      subtitle: t('withdrawCrypto.methodSubtitle'),
      config: {
        ...config,
        sourceToken,
        sourceTokens: [{ token: sourceToken, iconSrc: sourceOption.iconSrc }],
        // Unknown is not zero — omitted, validation skips the check rather
        // than rejecting every amount. Submit re-reads the real maximum.
        withdrawalSourceTokenBalance: hasMaximum
          ? () =>
              getMaxEnterableAmount({
                maxWithdrawableX18: maxWithdrawableRef.current,
                sequencerFee: sequencerFee.x18,
                decimals: sourceOption.decimals,
              })
          : undefined,
        defaultReceiveToken: config.defaultReceiveToken ?? sourceOption.symbol,
        getMinWithdrawalAmount:
          config.getMinWithdrawalAmount ?? (() => NADO_MIN_WITHDRAWAL_TOKENS),
        // Taken off the amount before quoting, and billed as source-chain cost.
        quoteInputFeeTokens: sequencerFee.tokenAmount,
        extraSourceGasUsd: sequencerFee.tokenAmount * sourceTokenUsdPrice,
        submitWithdrawal: createNadoWithdrawalSubmit({
          nado,
          subaccount,
          sourceToken: sourceOption,
          wallet: config.withdrawalWallet,
          onWithdrawn: invalidateMaxWithdrawable,
        }),
      },
    },
  ]
}

function useNadoSubaccount(
  nado: NadoWithdrawalFlowConfig | undefined,
): Hex | undefined {
  const { walletAddress, subaccountName } = nado ?? {}
  return useMemo(
    () => resolveNadoSubaccount({ walletAddress, subaccountName, logger }),
    [walletAddress, subaccountName],
  )
}
